import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Modal,
  ModalDialog,
  Typography,
  Button,
  Table,
  Input,
  FormControl,
  FormLabel,
  Alert,
  Card,
  Select,
  Option
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import { hasExpiry, implantNoteForAction } from '../util/stockChangeNotes';
import SizeSelect from './SizeSelect';
import StockDetailLayout from './StockDetailLayout';
import ResponsiveDetailTable from './ResponsiveDetailTable';
import DetailFieldCard from './DetailFieldCard';
import VendorSelect from './VendorSelect';
import AddVendorModal from './AddVendorModal';

const today = () => new Date().toISOString().slice(0, 10);
const nowDateTimeLocal = () => {
  const d = new Date();
  const date = d.toISOString().slice(0, 10);
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${date}T${time}`;
};

const ImplantPopup = ({ implantName, onClose, variant = 'modal' }) => {
  const isPage = variant === 'page';
  const [batches, setBatches] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [consumptionModalBatch, setConsumptionModalBatch] = useState(null);
  const [consumptionForm, setConsumptionForm] = useState({ qty: '', datetime: nowDateTimeLocal(), patient_name: '' });
  const [consumingBatchId, setConsumingBatchId] = useState(null);
  const [showAddStock, setShowAddStock] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [addStockForm, setAddStockForm] = useState({
    purchase_date: today(),
    quantity: '',
    rate: '',
    expiry_date: '',
    size_id: '',
    vendor_id: ''
  });
  const [addStockSubmitting, setAddStockSubmitting] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [editForm, setEditForm] = useState({
    implant_name: '',
    purchase_date: '',
    quantity: '',
    rate: '',
    expiry_date: '',
    size_id: '',
    vendor_id: ''
  });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [showReturnConfirm, setShowReturnConfirm] = useState(false);
  const [showDisposedConfirm, setShowDisposedConfirm] = useState(false);
  const [editingHistoryItem, setEditingHistoryItem] = useState(null);
  const [historyEditForm, setHistoryEditForm] = useState({ changeAmount: '', date: '', time: '00:00', changeNote: '' });
  const [historyEditSubmitting, setHistoryEditSubmitting] = useState(false);
  const [historyDeleteConfirm, setHistoryDeleteConfirm] = useState(null);

  const fetchHistory = useCallback(async (batchesToUse) => {
    try {
      const historyPromises = batchesToUse.map((batch) =>
        axiosInstance.get(`/implants/${batch.id}/history`)
      );
      const responses = await Promise.all(historyPromises);
      const allHistory = responses.flatMap((res, i) => {
        const list = res.data.history || [];
        const batchId = batchesToUse[i]?.id;
        return list.map((h) => ({ ...h, implant_id: h.implant_id ?? batchId }));
      });
      allHistory.sort((a, b) => (b.id - a.id));
      setHistory(allHistory);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      setVendorsLoading(true);
      const response = await axiosInstance.get('/vendors');
      setVendors(response.data.vendors || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load vendors');
    } finally {
      setVendorsLoading(false);
    }
  }, []);

  const handleVendorCreated = (vendor) => {
    setVendors((prev) => [...prev, vendor]);
    setAddStockForm((prev) => ({ ...prev, vendor_id: String(vendor.id) }));
    setAddVendorOpen(false);
  };

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(`/implants/batches/${encodeURIComponent(implantName)}`);
      const fetchedBatches = response.data.batches || [];
      setBatches(fetchedBatches);

      if (fetchedBatches.length > 0) {
        fetchHistory(fetchedBatches);
      } else {
        setHistory([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch batches');
    } finally {
      setLoading(false);
    }
  }, [implantName, fetchHistory]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  useEffect(() => {
    if ((showAddStock || editingBatch) && vendors.length === 0) {
      fetchVendors();
    }
  }, [showAddStock, editingBatch, vendors.length, fetchVendors]);

  const openEditModal = (batch) => {
    setEditingBatch(batch);
    setEditForm({
      implant_name: implantName,
      purchase_date: batch.purchase_date ? batch.purchase_date.slice(0, 10) : '',
      quantity: String(batch.quantity ?? ''),
      rate: batch.rate != null && batch.rate !== '' ? String(batch.rate) : '',
      expiry_date: batch.expiry_date ? batch.expiry_date.slice(0, 10) : '',
      size_id: batch.size_id != null ? String(batch.size_id) : '',
      vendor_id: batch.vendor_id != null ? String(batch.vendor_id) : ''
    });
    setError('');
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target || e;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingBatch) return;
    if (!editForm.purchase_date) {
      setError('Purchase date is required');
      return;
    }
    const qty = parseInt(editForm.quantity, 10);
    if (Number.isNaN(qty) || qty < 0) {
      setError('Quantity must be 0 or greater');
      return;
    }
    if (!editForm.vendor_id) {
      setError('Vendor is required');
      return;
    }
    const trimmedName = editForm.implant_name?.trim();
    if (!trimmedName) {
      setError('Implant name is required');
      return;
    }
    try {
      setEditSubmitting(true);
      setError('');
      await axiosInstance.put(`/implants/${editingBatch.id}`, {
        implant_name: trimmedName,
        date_entered: editForm.purchase_date,
        quantity: qty,
        rate: editForm.rate !== '' ? editForm.rate : null,
        expiry_date: editForm.expiry_date || null,
        size_id: editForm.size_id || null,
        vendor_id: parseInt(editForm.vendor_id, 10)
      });
      setEditingBatch(null);
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update batch');
    } finally {
      setEditSubmitting(false);
    }
  };

  const openConsumptionModal = (batch) => {
    setConsumptionModalBatch(batch);
    setConsumptionForm({ qty: '', datetime: nowDateTimeLocal(), patient_name: '' });
    setShowReturnConfirm(false);
    setShowDisposedConfirm(false);
    setError('');
  };

  const handleReturnConfirm = () => {
    handleConsumptionSubmit('returned');
    setShowReturnConfirm(false);
  };

  const handleDisposedConfirm = () => {
    handleConsumptionSubmit('disposed');
    setShowDisposedConfirm(false);
  };

  const isQtyFormValid = (batch) => {
    const n = parseInt(consumptionForm.qty, 10);
    return !Number.isNaN(n) && n > 0 && n <= Number(batch?.quantity || 0);
  };

  const handleConsumptionSubmit = async (action) => {
    if (!consumptionModalBatch) return;
    const num = parseInt(consumptionForm.qty, 10);
    if (!num || num <= 0) {
      setError('Enter a valid quantity');
      return;
    }
    if (action === 'consumed' && isExpired(consumptionModalBatch.expiry_date)) {
      setError('Expired stock cannot be consumed; use Return or Disposed');
      return;
    }
    if (num > consumptionModalBatch.quantity) {
      setError('Quantity cannot exceed current stock');
      return;
    }
    if (action === 'consumed' && !consumptionForm.patient_name?.trim()) {
      setError('Patient name is required when consuming');
      return;
    }
    const raw = (consumptionForm.datetime || nowDateTimeLocal()).trim();
    const datePart = raw.slice(0, 10);
    if (datePart > today()) {
      setError('Date cannot be in the future');
      return;
    }
    const consumptionDateTime = raw.includes('T')
      ? `${raw.slice(0, 10)} ${raw.slice(11, 16)}:00`
      : `${datePart} 00:00:00`;
    const changeAmount = -Math.abs(num);
    const changeNote = implantNoteForAction(action);
    const failLabels = { returned: 'return', disposed: 'disposal', consumed: 'consumption' };
    try {
      setConsumingBatchId(consumptionModalBatch.id);
      setError('');
      await axiosInstance.put(`/implants/${consumptionModalBatch.id}/quantity`, {
        changeAmount,
        consumptionDate: consumptionDateTime,
        changeNote,
        patient_name: action === 'consumed' ? consumptionForm.patient_name?.trim() || null : null
      });
      setConsumptionModalBatch(null);
      setConsumptionForm({ qty: '', datetime: nowDateTimeLocal(), patient_name: '' });
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to record ${failLabels[action] || 'change'}`);
    } finally {
      setConsumingBatchId(null);
    }
  };

  const renderBatchStockActions = (batch) => (
    <Button
      size="sm"
      variant="outlined"
      color="neutral"
      onClick={() => openConsumptionModal(batch)}
      loading={consumingBatchId === batch.id}
    >
      Consumed
    </Button>
  );

  const handleAddStockChange = (e) => {
    const { name, value } = e.target || e;
    setAddStockForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddStockSubmit = async (e) => {
    e.preventDefault();
    if (!addStockForm.quantity || !addStockForm.vendor_id || parseInt(addStockForm.quantity, 10) <= 0) {
      setError('Quantity and vendor are required');
      return;
    }
    if (!addStockForm.size_id) {
      setError('Size is required');
      return;
    }
    try {
      setAddStockSubmitting(true);
      setError('');
      await axiosInstance.post('/implants', {
        implant_name: implantName,
        date_entered: addStockForm.purchase_date,
        expiry_date: addStockForm.expiry_date || null,
        quantity: parseInt(addStockForm.quantity, 10),
        rate: addStockForm.rate !== '' ? addStockForm.rate : null,
        size_id: addStockForm.size_id || null,
        vendor_id: parseInt(addStockForm.vendor_id, 10)
      });
      setShowAddStock(false);
      setAddStockForm({
        purchase_date: today(),
        quantity: '',
        rate: '',
        expiry_date: '',
        size_id: '',
        vendor_id: ''
      });
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add stock');
    } finally {
      setAddStockSubmitting(false);
    }
  };

  const openHistoryEdit = (item) => {
    setEditingHistoryItem(item);
    let timeStr = '00:00';
    if (item.date) {
      const d = new Date(item.date);
      if (!Number.isNaN(d.getTime())) {
        timeStr = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      }
    }
    setHistoryEditForm({
      changeAmount: String(item.change_amount),
      date: item.date ? item.date.slice(0, 10) : today(),
      time: timeStr,
      changeNote: item.change_note || ''
    });
    setError('');
  };

  const handleHistoryEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingHistoryItem || editingHistoryItem.implant_id == null) return;
    const amount = parseInt(historyEditForm.changeAmount, 10);
    if (Number.isNaN(amount)) {
      setError('Quantity change must be a number');
      return;
    }
    try {
      setHistoryEditSubmitting(true);
      setError('');
      const datePart = historyEditForm.date || today();
      const timePart = (historyEditForm.time || '00:00').trim();
      const changeDateTime = timePart.match(/^\d{1,2}:\d{2}$/) ? `${datePart} ${timePart}:00` : `${datePart} 00:00:00`;
      await axiosInstance.put(
        `/implants/${editingHistoryItem.implant_id}/history/${editingHistoryItem.id}`,
        {
          changeAmount: amount,
          changeDate: changeDateTime,
          changeNote: historyEditForm.changeNote?.trim() || null
        }
      );
      setEditingHistoryItem(null);
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update history');
    } finally {
      setHistoryEditSubmitting(false);
    }
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const getDaysToExpire = (expiryDate, asOfDate) => {
    if (!expiryDate) return '—';
    const expiry = new Date(expiryDate);
    const ref = asOfDate ? new Date(asOfDate) : new Date();
    const ms = expiry.getTime() - ref.getTime();
    const days = Math.round(ms / (24 * 60 * 60 * 1000));
    if (days > 0) return `${days} day${days !== 1 ? 's' : ''}`;
    if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} ago`;
    return 'Expires today';
  };

  const handleHistoryDelete = async () => {
    if (!historyDeleteConfirm) return;
    const { implant_id, id } = historyDeleteConfirm;
    try {
      setError('');
      await axiosInstance.delete(`/implants/${implant_id}/history/${id}`);
      setHistoryDeleteConfirm(null);
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete history');
    } finally {
      setHistoryDeleteConfirm(null);
    }
  };

  const activeBatches = batches.filter((b) => Number(b.quantity) > 0);

  const detailBody = (
    <>
          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Card sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography level="h3">Batches</Typography>
              <Button
                size="sm"
                onClick={() => setShowAddStock((v) => !v)}
                sx={{
                  backgroundColor: 'primary.500',
                  '&:hover': { backgroundColor: 'primary.600' }
                }}
              >
                {showAddStock ? 'Cancel' : 'Add stock'}
              </Button>
            </Box>

            {showAddStock && (
              <Card variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'primary.50' }}>
                <Typography level="h4" sx={{ mb: 2, color: 'primary.500' }}>
                  Add new batch
                </Typography>
                <form onSubmit={handleAddStockSubmit}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <FormControl sx={{ minWidth: 140 }}>
                      <FormLabel>Implant</FormLabel>
                      <Input value={implantName} disabled size="sm" />
                    </FormControl>
                    <FormControl sx={{ minWidth: 140 }}>
                      <FormLabel>Purchase Date</FormLabel>
                      <Input
                        type="date"
                        name="purchase_date"
                        value={addStockForm.purchase_date}
                        onChange={handleAddStockChange}
                        size="sm"
                        required
                        sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                      />
                    </FormControl>
                    <FormControl sx={{ width: 100 }}>
                      <FormLabel>Quantity</FormLabel>
                      <Input
                        type="number"
                        name="quantity"
                        value={addStockForm.quantity}
                        onChange={handleAddStockChange}
                        size="sm"
                        min="1"
                        required
                        placeholder="0"
                        sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                      />
                    </FormControl>
                    <FormControl sx={{ minWidth: 140 }}>
                      <FormLabel>Expiry Date</FormLabel>
                      <Input
                        type="date"
                        name="expiry_date"
                        value={addStockForm.expiry_date}
                        onChange={handleAddStockChange}
                        size="sm"
                        sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                      />
                    </FormControl>
                    <SizeSelect
                      value={addStockForm.size_id}
                      onChange={(val) => setAddStockForm((prev) => ({ ...prev, size_id: val }))}
                      label="Size"
                      required
                      inputSize="sm"
                      sx={{ minWidth: 180 }}
                    />
                    <FormControl sx={{ width: 100 }}>
                      <FormLabel>Rate (₹)</FormLabel>
                      <Input
                        type="number"
                        name="rate"
                        value={addStockForm.rate}
                        onChange={handleAddStockChange}
                        size="sm"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                      />
                    </FormControl>
                    <VendorSelect
                      vendors={vendors}
                      value={addStockForm.vendor_id || ''}
                      onChange={(vendorId) => setAddStockForm((prev) => ({ ...prev, vendor_id: vendorId }))}
                      onAddNew={() => setAddVendorOpen(true)}
                      loading={vendorsLoading}
                      required
                      sx={{ minWidth: 180, mb: 0 }}
                    />
                    <Button
                      type="submit"
                      size="sm"
                      loading={addStockSubmitting}
                      sx={{
                        alignSelf: 'flex-end',
                        backgroundColor: 'primary.500',
                        '&:hover': { backgroundColor: 'primary.600' }
                      }}
                    >
                      Add stock
                    </Button>
                  </Box>
                </form>
              </Card>
            )}

            <ResponsiveDetailTable
              mobile={isPage ? (
                loading ? (
                  <Typography sx={{ textAlign: 'center', py: 2 }}>Loading...</Typography>
                ) : activeBatches.length === 0 ? (
                  <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                    {batches.length === 0
                      ? 'No batches found. Use "Add stock" to add a new batch.'
                      : 'No batches with stock. Use "Add stock" to add a new batch.'}
                  </Typography>
                ) : (
                  activeBatches.map((batch) => (
                    <DetailFieldCard
                      key={batch.id}
                      highlight={isExpired(batch.expiry_date)}
                      title={batch.size_name || 'Batch'}
                      fields={[
                        {
                          label: 'Purchase Date',
                          value: batch.purchase_date ? new Date(batch.purchase_date).toLocaleDateString() : 'N/A'
                        },
                        {
                          label: 'Days to expire',
                          value: (
                            <Typography level="body-sm" sx={isExpired(batch.expiry_date) ? { color: 'danger.500', fontWeight: 600 } : { fontWeight: 500 }}>
                              {getDaysToExpire(batch.expiry_date)}
                            </Typography>
                          )
                        },
                        { label: 'Vendor', value: batch.vendor_name || 'N/A' },
                        { label: 'Rate', value: batch.rate != null ? Number(batch.rate).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—' },
                        { label: 'Quantity', value: batch.quantity },
                        { label: 'Last Updated', value: batch.last_updated ? new Date(batch.last_updated).toLocaleDateString() : 'N/A' },
                        { label: 'Created By', value: batch.created_by || '—' }
                      ]}
                      actions={(
                        <>
                          <Button size="sm" variant="outlined" color="neutral" onClick={() => openEditModal(batch)}>Edit</Button>
                          {renderBatchStockActions(batch)}
                        </>
                      )}
                    />
                  ))
                )
              ) : null}
            >
              <Table
                sx={{
                  '& thead th': {
                    backgroundColor: 'primary.500',
                    color: 'white',
                    fontWeight: 'bold',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                  },
                  '& tbody tr:nth-of-type(even)': { backgroundColor: '#f9f9f9' },
                  '& tbody tr:hover': { backgroundColor: 'primary.50' },
                  '& td': { wordWrap: 'break-word', whiteSpace: 'normal' }
                }}
              >
                <thead>
                  <tr>
                    <th>Purchase Date</th>
                    <th>Days to expire</th>
                    <th>Size</th>
                    <th>Vendor</th>
                    <th>Rate (per item)</th>
                    <th>Current Quantity</th>
                    <th>Last Updated</th>
                    <th>Created By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td>
                    </tr>
                  ) : activeBatches.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: 'center', padding: '2rem' }}>
                        {batches.length === 0
                          ? 'No batches found. Use "Add stock" to add a new batch.'
                          : 'No batches with stock. Use "Add stock" to add a new batch.'}
                      </td>
                    </tr>
                  ) : (
                    activeBatches.map((batch) => (
                      <tr
                        key={batch.id}
                        style={{
                          backgroundColor: isExpired(batch.expiry_date) ? '#ffebee' : 'transparent'
                        }}
                      >
                        <td>
                          {batch.purchase_date
                            ? new Date(batch.purchase_date).toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td>
                          <Typography
                            level="body-sm"
                            sx={isExpired(batch.expiry_date) ? { color: 'danger', fontWeight: 500 } : {}}
                          >
                            {getDaysToExpire(batch.expiry_date)}
                          </Typography>
                        </td>
                        <td>{batch.size_name || '—'}</td>
                        <td>{batch.vendor_name || 'N/A'}</td>
                        <td>{batch.rate != null ? Number(batch.rate).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}</td>
                        <td>{batch.quantity}</td>
                        <td>
                          {batch.last_updated
                            ? new Date(batch.last_updated).toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td>{batch.created_by || '—'}</td>
                        <td>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(batch);
                              }}
                            >
                              Edit
                            </Button>
                            {renderBatchStockActions(batch)}
                          </Box>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </ResponsiveDetailTable>
          </Card>

          <Card>
            <Typography level="h3" sx={{ mb: 2 }}>
              Change History
            </Typography>
            <ResponsiveDetailTable
              mobile={isPage ? (
                history.length === 0 ? (
                  <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>No history found</Typography>
                ) : (
                  history.map((item) => (
                    <DetailFieldCard
                      key={item.id}
                      title={new Date(item.date).toLocaleDateString()}
                      fields={[
                        { label: 'Vendor', value: item.vendor_name || 'N/A' },
                        { label: 'Patient', value: item.patient_name || '—' },
                        { label: 'Type', value: item.change_note || (item.change_amount > 0 ? 'Added' : 'Consumed') },
                        {
                          label: 'Qty Change',
                          value: (
                            <Typography level="body-sm" sx={{ fontWeight: 600, color: item.change_amount > 0 ? 'success.600' : 'danger.500' }}>
                              {item.change_amount > 0 ? '+' : ''}{item.change_amount}
                            </Typography>
                          )
                        },
                        { label: 'Available', value: item.new_quantity }
                      ]}
                      actions={(
                        <>
                          <Button size="sm" variant="plain" color="neutral" onClick={() => openHistoryEdit(item)}>Edit</Button>
                          <Button size="sm" variant="plain" color="danger" onClick={() => setHistoryDeleteConfirm(item)}>Delete</Button>
                        </>
                      )}
                    />
                  ))
                )
              ) : null}
            >
              <Table
                sx={{
                  '& thead th': {
                    backgroundColor: 'primary.500',
                    color: 'white',
                    fontWeight: 'bold',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                  },
                  '& tbody tr:nth-of-type(even)': { backgroundColor: '#f9f9f9' },
                  '& tbody tr:hover': { backgroundColor: 'primary.50' },
                  '& td': { wordWrap: 'break-word', whiteSpace: 'normal' }
                }}
              >
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Vendor</th>
                    <th>Patient</th>
                    <th>Type</th>
                    <th>Quantity Change</th>
                    <th>Available Quantity</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                        No history found
                      </td>
                    </tr>
                  ) : (
                    history.map((item) => (
                      <tr key={item.id}>
                        <td>{new Date(item.date).toLocaleDateString()}</td>
                        <td>{item.vendor_name || 'N/A'}</td>
                        <td>{item.patient_name || '—'}</td>
                        <td>{item.change_note || (item.change_amount > 0 ? 'Added' : 'Consumed')}</td>
                        <td
                          style={{
                            color: item.change_amount > 0 ? 'green' : 'red',
                            fontWeight: 'bold'
                          }}
                        >
                          {item.change_amount > 0 ? '+' : ''}
                          {item.change_amount}
                        </td>
                        <td>{item.new_quantity}</td>
                        <td>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Button size="sm" variant="plain" color="neutral" onClick={() => openHistoryEdit(item)}>
                              Edit
                            </Button>
                            <Button size="sm" variant="plain" color="danger" onClick={() => setHistoryDeleteConfirm(item)}>
                              Delete
                            </Button>
                          </Box>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </ResponsiveDetailTable>
          </Card>
    </>
  );

  return (
    <>
      {isPage ? (
        <StockDetailLayout title={implantName} onBack={onClose}>
          {loading ? <Typography>Loading...</Typography> : detailBody}
        </StockDetailLayout>
      ) : (
        <Modal open={true} onClose={onClose}>
          <ModalDialog
            sx={{
              maxWidth: '90vw',
              width: '1200px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography level="h2" sx={{ color: 'primary.500' }}>
                {implantName} - Batch Details
              </Typography>
              <Button variant="plain" onClick={onClose}>Close</Button>
            </Box>
            {detailBody}
          </ModalDialog>
        </Modal>
      )}

      {editingBatch && (
        <Modal open onClose={() => setEditingBatch(null)}>
          <ModalDialog sx={{ maxWidth: 600 }}>
            <Typography level="h4" sx={{ mb: 2, color: 'primary.500' }}>
              Edit batch
            </Typography>
            <form onSubmit={handleEditSubmit}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <FormControl sx={{ flex: 1, minWidth: 200 }} required>
                  <FormLabel>Implant Name</FormLabel>
                  <Input
                    type="text"
                    name="implant_name"
                    value={editForm.implant_name}
                    onChange={handleEditFormChange}
                    size="sm"
                    required
                    sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                  />
                </FormControl>
                <FormControl sx={{ minWidth: 140 }} required>
                  <FormLabel>Purchase Date</FormLabel>
                  <Input
                    type="date"
                    name="purchase_date"
                    value={editForm.purchase_date}
                    onChange={handleEditFormChange}
                    size="sm"
                    required
                    sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                  />
                </FormControl>
                <FormControl sx={{ minWidth: 140 }}>
                  <FormLabel>Expiry Date</FormLabel>
                  <Input
                    type="date"
                    name="expiry_date"
                    value={editForm.expiry_date}
                    onChange={handleEditFormChange}
                    required
                    size="sm"
                    sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                  />
                </FormControl>
                <FormControl sx={{ width: 100 }} required>
                  <FormLabel>Quantity</FormLabel>
                  <Input
                    type="number"
                    name="quantity"
                    value={editForm.quantity}
                    onChange={handleEditFormChange}
                    size="sm"
                    min="0"
                    required
                    placeholder="0"
                    sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                  />
                </FormControl>
                <SizeSelect
                  value={editForm.size_id}
                  onChange={(val) => setEditForm((prev) => ({ ...prev, size_id: val }))}
                  label="Size"
                  required
                  inputSize="sm"
                  sx={{ minWidth: 180 }}
                />
                <FormControl sx={{ width: 100 }}>
                  <FormLabel>Rate (₹)</FormLabel>
                  <Input
                    type="number"
                    name="rate"
                    value={editForm.rate}
                    onChange={handleEditFormChange}
                    size="sm"
                    min="0"
                    required
                    step="0.01"
                    placeholder="0"
                    sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                  />
                </FormControl>
                <FormControl sx={{ minWidth: 180 }} required>
                  <FormLabel>Vendor</FormLabel>
                  <Select
                    name="vendor_id"
                    value={editForm.vendor_id || ''}
                    onChange={(_, val) => setEditForm((prev) => ({ ...prev, vendor_id: val ?? '' }))}
                    size="sm"
                    placeholder={vendorsLoading ? 'Loading...' : 'Select vendor'}
                    disabled={vendorsLoading}
                    required
                    sx={{ minWidth: 160, '&.Joy-focused': { borderColor: 'primary.500' } }}
                  >
                    {editingBatch?.vendor_id != null && editingBatch.vendor_name && !vendors.some((v) => Number(v.id) === Number(editingBatch.vendor_id)) && (
                      <Option value={String(editingBatch.vendor_id)}>{editingBatch.vendor_name}</Option>
                    )}
                    {vendors.map((v) => (
                      <Option key={v.id} value={String(v.id)}>{v.name}</Option>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                <Button type="button" variant="plain" color="neutral" onClick={() => setEditingBatch(null)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={editSubmitting}
                  sx={{ backgroundColor: 'primary.500', '&:hover': { backgroundColor: 'primary.600' } }}
                >
                  Save
                </Button>
              </Box>
            </form>
          </ModalDialog>
        </Modal>
      )}

      {consumptionModalBatch && (
        <Modal open onClose={() => setConsumptionModalBatch(null)}>
          <ModalDialog sx={{ maxWidth: 400 }}>
            <Typography level="h4" sx={{ mb: 2, color: 'primary.500' }}>
              {isExpired(consumptionModalBatch.expiry_date)
                ? 'Return or dispose (expired – cannot consume)'
                : hasExpiry(consumptionModalBatch)
                  ? 'Use stock'
                  : 'Consumed'}
            </Typography>
            <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
              Batch: {consumptionModalBatch.purchase_date ? new Date(consumptionModalBatch.purchase_date).toLocaleDateString() : 'N/A'}
              {consumptionModalBatch.size_name ? ` · Size: ${consumptionModalBatch.size_name}` : ''}
              {' · '}Current qty: {consumptionModalBatch.quantity}
              {isExpired(consumptionModalBatch.expiry_date) && (
                <Typography component="span" level="body-sm" sx={{ color: 'danger', ml: 0.5 }}>
                  · Expired
                </Typography>
              )}
            </Typography>
            <Box component="form" onSubmit={(e) => e.preventDefault()}>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Quantity * (max {consumptionModalBatch.quantity})</FormLabel>
                <Input
                  type="number"
                  value={consumptionForm.qty}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setConsumptionForm((prev) => ({ ...prev, qty: '' }));
                      return;
                    }
                    const num = parseInt(raw, 10);
                    const available = Number(consumptionModalBatch.quantity) || 0;
                    if (!Number.isNaN(num) && num > available) {
                      setConsumptionForm((prev) => ({ ...prev, qty: String(available) }));
                    } else {
                      setConsumptionForm((prev) => ({ ...prev, qty: raw }));
                    }
                  }}
                  min={1}
                  max={consumptionModalBatch.quantity}
                  placeholder="0"
                  required
                  sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Patient name (required for Consumed)</FormLabel>
                <Input
                  value={consumptionForm.patient_name}
                  onChange={(e) => setConsumptionForm((prev) => ({ ...prev, patient_name: e.target.value }))}
                  placeholder="Patient name"
                  sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Date & time</FormLabel>
                <Input
                  type="datetime-local"
                  value={consumptionForm.datetime}
                  max={`${today()}T23:59`}
                  onChange={(e) => setConsumptionForm((prev) => ({ ...prev, datetime: e.target.value }))}
                  required
                  sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                />
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <Button type="button" variant="plain" color="neutral" onClick={() => setConsumptionModalBatch(null)}>
                  Cancel
                </Button>
                {hasExpiry(consumptionModalBatch) && (
                  <>
                    <Button
                      type="button"
                      variant="outlined"
                      color="success"
                      onClick={() => setShowReturnConfirm(true)}
                      loading={consumingBatchId === consumptionModalBatch.id}
                      disabled={!isQtyFormValid(consumptionModalBatch)}
                    >
                      Return
                    </Button>
                    <Button
                      type="button"
                      variant="outlined"
                      color="warning"
                      onClick={() => setShowDisposedConfirm(true)}
                      loading={consumingBatchId === consumptionModalBatch.id}
                      disabled={!isQtyFormValid(consumptionModalBatch)}
                    >
                      Disposed
                    </Button>
                  </>
                )}
                {!isExpired(consumptionModalBatch.expiry_date) && (
                  <Button
                    type="button"
                    sx={{ backgroundColor: 'primary.500', '&:hover': { backgroundColor: 'primary.600' } }}
                    onClick={() => handleConsumptionSubmit('consumed')}
                    loading={consumingBatchId === consumptionModalBatch.id}
                    disabled={!isQtyFormValid(consumptionModalBatch) || !consumptionForm.patient_name?.trim()}
                  >
                    Consumed
                  </Button>
                )}
              </Box>
            </Box>
          </ModalDialog>
        </Modal>
      )}

      <Modal open={showReturnConfirm} onClose={() => setShowReturnConfirm(false)} sx={{ zIndex: 1400 }} slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.6)' } } }}>
        <ModalDialog layout="center" variant="soft" color="warning" sx={{ maxWidth: 400, boxShadow: 'lg', p: 3 }}>
          <Typography level="h4" sx={{ mb: 2 }}>Confirm return</Typography>
          <Typography sx={{ mb: 3 }}>Confirm return of {consumptionForm.qty || '0'} to vendor? Stock will be reduced.</Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="plain" color="neutral" onClick={() => setShowReturnConfirm(false)}>Cancel</Button>
            <Button variant="solid" color="warning" onClick={handleReturnConfirm} loading={consumingBatchId === consumptionModalBatch?.id}>Confirm return</Button>
          </Box>
        </ModalDialog>
      </Modal>

      <Modal open={showDisposedConfirm} onClose={() => setShowDisposedConfirm(false)} sx={{ zIndex: 1400 }} slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.6)' } } }}>
        <ModalDialog layout="center" variant="soft" color="warning" sx={{ maxWidth: 400, boxShadow: 'lg', p: 3 }}>
          <Typography level="h4" sx={{ mb: 2 }}>Confirm disposal</Typography>
          <Typography sx={{ mb: 3 }}>Confirm disposal of {consumptionForm.qty || '0'} quantity? Stock will be written off and reduced.</Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="plain" color="neutral" onClick={() => setShowDisposedConfirm(false)}>Cancel</Button>
            <Button variant="solid" color="warning" onClick={handleDisposedConfirm} loading={consumingBatchId === consumptionModalBatch?.id}>Confirm disposal</Button>
          </Box>
        </ModalDialog>
      </Modal>

      <Modal open={Boolean(historyDeleteConfirm)} onClose={() => setHistoryDeleteConfirm(null)} sx={{ zIndex: 1400 }} slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.6)' } } }}>
        <ModalDialog layout="center" variant="soft" color="danger" sx={{ maxWidth: 400, boxShadow: 'lg', p: 3 }}>
          <Typography level="h4" sx={{ mb: 2 }}>Delete history record</Typography>
          <Typography sx={{ mb: 3 }}>Delete this history record? Stock will be adjusted accordingly.</Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="plain" color="neutral" onClick={() => setHistoryDeleteConfirm(null)}>Cancel</Button>
            <Button variant="solid" color="danger" onClick={handleHistoryDelete}>Delete</Button>
          </Box>
        </ModalDialog>
      </Modal>

      {editingHistoryItem && (
        <Modal open onClose={() => setEditingHistoryItem(null)}>
          <ModalDialog sx={{ maxWidth: 440 }}>
            <Typography level="h4" sx={{ mb: 2, color: 'primary.500' }}>Edit history record</Typography>
            <form onSubmit={handleHistoryEditSubmit}>
              <FormControl sx={{ mb: 2 }} fullWidth>
                <FormLabel>Quantity change</FormLabel>
                <Input
                  type="number"
                  value={historyEditForm.changeAmount}
                  onChange={(e) => setHistoryEditForm((prev) => ({ ...prev, changeAmount: e.target.value }))}
                  required
                  sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }} fullWidth>
                <FormLabel>Date</FormLabel>
                <Input
                  type="date"
                  value={historyEditForm.date}
                  onChange={(e) => setHistoryEditForm((prev) => ({ ...prev, date: e.target.value }))}
                  max={today()}
                  required
                  sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }} fullWidth>
                <FormLabel>Time</FormLabel>
                <Input
                  type="time"
                  value={historyEditForm.time}
                  onChange={(e) => setHistoryEditForm((prev) => ({ ...prev, time: e.target.value }))}
                  required
                  sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }} fullWidth>
                <FormLabel>Type / Note</FormLabel>
                <Input
                  value={historyEditForm.changeNote}
                  onChange={(e) => setHistoryEditForm((prev) => ({ ...prev, changeNote: e.target.value }))}
                  placeholder="e.g. Consumed, Returned"
                  required
                  sx={{ '&:focus-within': { borderColor: 'primary.500' } }}
                />
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button type="button" variant="plain" color="neutral" onClick={() => setEditingHistoryItem(null)}>Cancel</Button>
                <Button type="submit" loading={historyEditSubmitting} sx={{ backgroundColor: 'primary.500', '&:hover': { backgroundColor: 'primary.600' } }}>Save</Button>
              </Box>
            </form>
          </ModalDialog>
        </Modal>
      )}

      <AddVendorModal
        open={addVendorOpen}
        onClose={() => setAddVendorOpen(false)}
        onCreated={handleVendorCreated}
      />
    </>
  );
};

export default ImplantPopup;
